import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Form, Table, Button, Badge, Modal } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import Select from "react-dropdown-select";
import useDebounce from "../../hooks/useDebounce";
import FormInput from "../../Components/FormInput";
import Preloader from "../../Components/Preloader";
import AddCaseGenerated from "../../Components/FormComponent/AddCaseGenerated";
import AddCaseAllocation from "../../Components/FormComponent/AddCaseAllocation";
import UpdateCaseDetails from "../../Components/FormComponent/UpdateCaseDetails";
import AddCaseDocument from "../../Components/FormComponent/AddCaseDocument";
import AddCaseActivity from "../../Components/FormComponent/AddCaseActivity";
import ViewCaseChecklist from "../../Components/FormComponent/ViewCaseChecklist";
import AddCaseGenerateInitialReport from "../../Components/FormComponent/AddCaseGenerateInitialReport";
import FilterModal from "../../Components/FilterModal";
import DocumentViewerModal from "../../Components/DocumentViewerModal";
import useCaseDetailsContext from "../../hooks/useCaseDetailsContext";
import { getDynamicReportPath } from "../../util/caseCategoryNavigation";
import { getGeneralSettingList } from "../../redux/actions/GeneralSettingActions";
import { getMasterAllUserList, getMasterAllCheckSubList } from "../../redux/actions/MasterActions";
import { FaStar } from "react-icons/fa";
import { docUrl } from "../../axios";
import { getDateTimeMysqlFormat, getDateTimeMysqlGivenFormatYmd } from "../../util/generalUtills";
import swal from "sweetalert2";

const CaseDetails = ({ case_url, title, show }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    pathname,
    actions,
    caseDetailsLists,
    loadingCaseDetails,
    errorsCaseDetails,
    changedCaseDetailsStatus,
    dynamicReportStatus,
    clearDynamicReportType,
    resetStatusType,
    isMotor,
  } = useCaseDetailsContext();
  const [showModal, setShowModal] = useState(false);
  const [reportGenerationRequested, setReportGenerationRequested] = useState(false);
  const [updateCaseDetailsId, setUpdateCaseDetailsId] = useState(null);
  const [formData, setFormData] = useState({});
  const fileInputRef = useRef(null);
  const qcFileInputRef = useRef(null);

  // Document viewer modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({ url: '', fileName: '' });

  // Internal Trigger modal state
  const [showInternalTriggerModal, setShowInternalTriggerModal] = useState(false);
  const [internalTriggerText, setInternalTriggerText] = useState("");
  const [triggersList, setTriggersList] = useState([]);
  const [internalTriggerError, setInternalTriggerError] = useState("");

  const statusList = [
    { label: t("Approved"), value: "Approved" },
    { label: t("Rejected"), value: "Rejected" },
  ];

  const qcStatusList = [
    { label: t("Approved"), value: "Approved" },
    { label: t("Rejected"), value: "Rejected" },
    { label: t("Send Back to Doctor"), value: "Rejected" },
  ];

  const showQcDocumentsUpload = formData.qc_case_status_label === t("Send Back to Doctor");

  const isClosedStatusList = [
    { label: t("Yes"), value: "Yes" },
    { label: t("No"), value: "No" },
  ];



  const [showCaseGeneratedModel, setShowCaseGeneratedModel] = useState(false);
  const hideCaseGeneratedModel = () => {
    setShowCaseGeneratedModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };

  // Document viewer modal handlers
  const handleDocumentClick = (documentUrl, fileName) => {
    const name = String(fileName ?? "");
    const url = String(documentUrl ?? "");
    const lowerName = name.toLowerCase();
    const lowerUrl = url.toLowerCase();

    // Docx can't be reliably previewed in browser.
    // For S3/cross-origin URLs, programmatic download is often blocked/ignored,
    // but opening in a new tab works (server returns downloadable response).
    if (lowerName.endsWith(".docx") || lowerUrl.endsWith(".docx")) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    setSelectedDocument({ url: documentUrl, fileName: fileName });
    setShowDocumentModal(true);
  };

  const hideDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument({ url: '', fileName: '' });
  };


  const [showCaseAllocationModel, setShowCaseAllocationModel] = useState(false);
  const [editAllocationId, setEditAllocationId] = useState(null);
  const hideCaseAllocationModel = () => {
    setShowCaseAllocationModel(false);
    setEditAllocationId(null);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };
  const handleEditAllocation = (allocationId) => {
    setEditAllocationId(allocationId);
    setShowCaseAllocationModel(true);
  };


  const [showUpdateCaseDetailsModel, setShowUpdateCaseDetailsModel] = useState(false);
  const hideUpdateCaseDetailsModel = () => {
    setShowUpdateCaseDetailsModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };


  const [showAddCaseDocumentModel, setShowAddCaseDocumentModel] = useState(false);
  const hideAddCaseDocumentModel = () => {
    setShowAddCaseDocumentModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };


  const [showCaseActivityModel, setShowCaseActivityModel] = useState(false);
  const hideCaseActivityModel = () => {
    setShowCaseActivityModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };


  const [showAddCaseGenerateInitialReportModel, setShowAddCaseGenerateInitialReportModel] = useState(false);
  const hideAddCaseGenerateInitialReportModel = () => {
    setShowAddCaseGenerateInitialReportModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };


  const [showViewCaseChecklistModel, setShowViewCaseChecklistModel] = useState(false);
  const hideViewCaseChecklistModel = () => {
    setShowViewCaseChecklistModel(false);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedRevokeAllocation, setSelectedRevokeAllocation] = useState(null);
  const [revokeRemark, setRevokeRemark] = useState("");

  const [showCaseStatusModal, setShowCaseStatusModal] = useState(false);
  const [caseStatusModalType, setCaseStatusModalType] = useState(null);
  const [caseStatusRemark, setCaseStatusRemark] = useState("");

  const {
    AuthReducer: { auth, errors, loading },
    masterReducer: { masterAllUserLists, masterUserLists, loadingMasterAllUserLists, errorsMasterAllUserLists, masterAllCheckSubLists },
    caseDetailsReducer: {
      caseDetailsPermissions,
      caseDetailsCount,
    },
    generalSettingReducer: { generalSettingLists },
  } = useSelector((state) => state);

  const refreshCaseDetails = useCallback(() => {
    if (auth?.token && id) {
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    }
  }, [auth?.token, id, pathname, dispatch, actions]);

  const qcEnable = useMemo(() => {
    if (!generalSettingLists || Object.keys(generalSettingLists).length === 0) {
      return null;
    }
    return (
      generalSettingLists.qc_enable === true ||
      generalSettingLists.qc_enable === 1
    );
  }, [generalSettingLists]);

  const reportFormatUrl =
    caseDetailsLists?.reportFormatUrl ||
    caseDetailsLists?.data?.reportFormatUrl ||
    "";
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;

  const hideModel = () => {
    setShowModal(false);
    setUpdateCaseDetailsId(null);
    dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
  };
  console.log('masterUserLists', masterUserLists);

  useEffect(() => {
    if (caseDetailsLists?.case_idInternalTriggerList?.length > 0) {
      setTriggersList(caseDetailsLists?.case_idInternalTriggerList?.map(item => ({
        trigger: JSON.parse(item?.internal_trigger),
        createdBy: item?.createdByInternalTrigger?.name || item?.createdByInternalTrigger?.name,
        createdAt: getDateTimeMysqlFormat(item?.createdAt)
      })));
    }
  }, [caseDetailsLists?.case_idInternalTriggerList]);

  const fetchCaseDetailsList = useCallback(() => {
    refreshCaseDetails();
  }, [refreshCaseDetails]);

  useDebounce(fetchCaseDetailsList, 500, [auth, paginate, page, search, sort]);

  useEffect(() => {
    if (auth?.token) {
      dispatch(getGeneralSettingList(auth.token, 10, 1, "", "desc"));
    }
  }, [auth?.token, dispatch]);

  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors])

  const [validated, setValidated] = useState(false);

  const handleSubmitBackend = (event) => {
    const form = event?.target?.closest?.("form");
    if (form && form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      setValidated(true);
      return;
    }
    setValidated(true);

    const updatedFormData = { ...formData, case_id: id };

    const multipartData = new FormData();
    Object.entries(updatedFormData).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      multipartData.append(key, value);
    });

    dispatch(actions.saveInitialReportDocuments(auth?.token, multipartData)).then(() => {
      setFormData({});
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  useEffect(() => {
    if (show && auth?.token) {
      dispatch(getMasterAllUserList(auth?.token));
    }
  }, [show, auth?.token, dispatch]);

  // Load all sub-checklist items (for checklist assignment checking)
  useEffect(() => {
    if (auth?.token) {
      dispatch(getMasterAllCheckSubList(auth.token));
    }
  }, [auth?.token, dispatch]);

  useEffect(() => {
    if (changedCaseDetailsStatus && Object.keys(changedCaseDetailsStatus).length > 0) {
      if (!changedCaseDetailsStatus?.success) {
        refreshCaseDetails();
      }
      setInternalTriggerText("");
      setInternalTriggerError("");
    }
  }, [changedCaseDetailsStatus, refreshCaseDetails])

  const anyCaseFormModalOpen =
    showCaseAllocationModel ||
    showAddCaseDocumentModel ||
    showUpdateCaseDetailsModel ||
    showCaseActivityModel ||
    showAddCaseGenerateInitialReportModel ||
    showViewCaseChecklistModel ||
    showCaseGeneratedModel;

  useEffect(() => {
    if (!reportGenerationRequested) return;
    if (dynamicReportStatus && Object.keys(dynamicReportStatus).length > 0 && dynamicReportStatus?.success) {
      dispatch({ type: clearDynamicReportType });
      setReportGenerationRequested(false);
      navigate(getDynamicReportPath(id, pathname));
    }
  }, [reportGenerationRequested, dynamicReportStatus, dispatch, navigate, id, pathname, clearDynamicReportType])

  useEffect(() => {
    if (!reportGenerationRequested) return;
    if (errorsCaseDetails?.status === 404) {
      toast.error("Report Template not found Please upload Initial Report");
      dispatch({ type: clearDynamicReportType });
      setReportGenerationRequested(false);
    }
  }, [reportGenerationRequested, errorsCaseDetails, dispatch, clearDynamicReportType]);

  const handleSubmitQC = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);

    const multipartData = new FormData();
    multipartData.append("case_id", id);

    if (formData.qc_case_status) {
      multipartData.append("qc_case_status", formData.qc_case_status);
    }
    if (formData.qc_case_remark) {
      multipartData.append("qc_case_remark", formData.qc_case_remark);
    }

    if (formData.qc_case_status_label === t("Send Back to Doctor")) {
      (formData.qc_documents || []).forEach((file) => {
        multipartData.append("qc_documents", file);
      });
    }

    dispatch(actions.saveQCStatusReports(auth?.token, multipartData)).then(() => {
      setFormData({});
      if (qcFileInputRef.current) {
        qcFileInputRef.current.value = "";
      }
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  const handleSubmitFieldQC = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);

    const updatedFormData = {
      ...formData,
      case_id: id,
    };

    dispatch(actions.saveFieldQCStatusReports(auth?.token, updatedFormData)).then(() => {
      setFormData({});
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  const handleSubmitIsClosed = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);

    const updatedFormData = {
      ...formData,
      case_id: id,
    };

    dispatch(actions.saveIsClosedReports(auth?.token, updatedFormData)).then(() => {
      setFormData({});
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  const handleOpenCaseStatusModal = (status) => {
    setCaseStatusModalType(status);
    setCaseStatusRemark("");
    setShowCaseStatusModal(true);
  };

  const handleCloseCaseStatusModal = () => {
    setShowCaseStatusModal(false);
    setCaseStatusModalType(null);
    setCaseStatusRemark("");
  };

  const handleSubmitCaseStatusModal = () => {
    const payload = { case_id: id, case_status: caseStatusModalType, remark: caseStatusRemark };
    dispatch(actions.updateCaseStatus(auth?.token, payload)).then(() => {
      handleCloseCaseStatusModal();
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  // Check if all checklist items are assigned to any investigator
  const allChecklistAssigned = useMemo(() => {
    const allSubItems =
      masterAllCheckSubLists
        ?.flatMap((item) => item?.checklist_subchecklist || []) || [];

    if (!allSubItems.length) return false;

    const assignedIds = new Set();

    caseDetailsLists?.caseIdCaseFieldOfficerAllocation?.forEach((allocation) => {
      allocation?.fieldOfficerCaseCheckListDocument?.forEach((doc) => {
        const subId = doc?.subCheckListCaseCheckListDocument?.id;
        if (subId) assignedIds.add(subId);
      });
    });

    // Return true only if every sub-checklist item exists in assignedIds
    return allSubItems.every((sub) => assignedIds.has(sub.id));
  }, [masterAllCheckSubLists, caseDetailsLists?.caseIdCaseFieldOfficerAllocation]);

  const handleOpenRevokeModal = (allocation) => {
    if (!allocation) {
      return;
    }

    const hasPendingChecklist = allocation?.fieldOfficerCaseCheckListDocument?.some(
      (doc) => doc?.status === "Pending"
    );

    if (!hasPendingChecklist) {
      return;
    }

    setSelectedRevokeAllocation(allocation);
    setRevokeRemark("");
    setShowRevokeModal(true);
  };

  const handleCloseRevokeModal = () => {
    setShowRevokeModal(false);
    setSelectedRevokeAllocation(null);
    setRevokeRemark("");
  };

  const handleRevokeAssignFieldOfficer = () => {
    if (!selectedRevokeAllocation) {
      return;
    }

    const hasPendingChecklist = selectedRevokeAllocation?.fieldOfficerCaseCheckListDocument?.some(
      (doc) => doc?.status === "Pending"
    );

    if (!hasPendingChecklist) {
      handleCloseRevokeModal();
      return;
    }

    const fieldOfficerId = selectedRevokeAllocation?.foIdCaseFieldOfficerAllocation?.id;

    if (!fieldOfficerId) {
      return;
    }

    const payload = {
      case_id: caseDetailsLists?.id || id,
      field_officer_id: fieldOfficerId,
      revoke_remark: revokeRemark?.trim(),
    };

    dispatch(actions.revokeAssignFieldOfficer(auth?.token, payload))
  };

  useEffect(() => {
    if (!changedCaseDetailsStatus?.success) return;
    if (anyCaseFormModalOpen) return;

    dispatch({ type: resetStatusType });

    swal.fire({
      title: "Success!",
      text: changedCaseDetailsStatus?.message,
      icon: "success",
      showConfirmButton: false,
      timer: 1500,
      didOpen: () => {
        document.querySelectorAll(".swal2-container").forEach((el) => {
          el.style.zIndex = "10050";
        });
      },
    }).then(() => {
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  }, [
    changedCaseDetailsStatus,
    anyCaseFormModalOpen,
    dispatch,
    resetStatusType,
    actions,
    auth?.token,
    id,
    pathname,
  ]);

  const handleSubmitCaseRating = () => {
    const updatedFormData = { ...formData, case_id: id };

    dispatch(actions.saveCaseRating(auth?.token, updatedFormData)).then(() => {
      setFormData({});
      dispatch(actions.fetchCaseDetailsById(auth.token, id, pathname));
    });
  };

  const [filters, setFilters] = useState({});
  const [modal, setModal] = useState({ show: false, key: "", type: "", value: "" });

  const handleFilterClick = (key) => {
    if (!filters[key]) {
      setFilters((prev) => ({
        ...prev,
        [key]: { type: "", value: "" },
      }));
    }
    setModal({ show: true, key, type: filters[key]?.type || "", value: filters[key]?.value || "" });
  };

  const handleModalChange = (field, value) => {
    setModal((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    setFilters((prev) => ({
      ...prev,
      [modal.key]: { type: modal.type, value: modal.value },
    }));
    setModal({ show: false, key: "", type: "", value: "" });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[key];
      return updatedFilters;
    });
    setModal({ show: false, key: "", type: "", value: "" });
  };

  const handleRating = (category, value) => {
    setFormData((prevRatings) => ({ ...prevRatings, [category]: value }));
  };

  const currentInitialReport = useMemo(() => {
    return caseDetailsLists?.case_idCaseInitialReportList?.find(
      (item) => item?.bofficer_id === auth?.data?.id
    );
  }, [caseDetailsLists?.case_idCaseInitialReportList, auth?.data?.id]);

  const renderQcDocuments = (documents = []) => {
    if (!Array.isArray(documents) || documents.length === 0) {
      return null;
    }

    return documents.map((doc, index) => (
      <Badge
        key={`${doc?.customFilename || doc?.url || index}-${index}`}
        bg="success"
        className="me-1 mb-1"
        style={{ cursor: "pointer", fontSize: "0.75rem" }}
        onClick={() => handleDocumentClick(doc?.url, doc?.originalname || doc?.customFilename)}
      >
        {doc?.originalname || doc?.customFilename}
      </Badge>
    ));
  };


  const renderStars = (category) => {
    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      return (
        <FaStar
          key={index}
          className="mx-1"
          color={starValue <= formData[category] ? "#ffc107" : "#e4e5e9"}
          onClick={() => handleRating(category, starValue)}
          style={{ cursor: "pointer", fontSize: "1.4rem" }}
        />
      );
    });
  };

  const inputHandler = (e) => {
    const { name, files } = e.target;

    if (files) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: e.target.value });
    }
  };

  const qcDocumentsHandler = (e) => {
    const files = Array.from(e.target.files || []);
    setFormData((prevFormData) => ({
      ...prevFormData,
      qc_documents: files,
    }));
  };

  const qcCaseStatusHandler = (selectedOption) => {
    const selectedValue = selectedOption?.value;
    const selectedLabel = selectedOption?.label;
    const isSendBackToDoctor = selectedLabel === t("Send Back to Doctor");

    setFormData((prevFormData) => {
      const nextFormData = {
        ...prevFormData,
        qc_case_status: selectedValue,
        qc_case_status_label: selectedLabel,
      };

      if (!isSendBackToDoctor) {
        delete nextFormData.qc_documents;
      }

      return nextFormData;
    });

    if (!isSendBackToDoctor && qcFileInputRef.current) {
      qcFileInputRef.current.value = "";
    }
  };

  const selectHandler2 = (selectedData, name) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: selectedData
    }));
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errorsCaseDetails?.data?.vehicle_model ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errorsCaseDetails?.data?.vehicle_model ? 'red' : base.borderColor,
      }
    }),
  };

  useEffect(() => {
    document.title = `Case Summary :: ${auth?.generalSettings?.title}`;
  }, []);

  const formatMotorDate = (value) => {
    if (!value) return "";
    try {
      return getDateTimeMysqlGivenFormatYmd(value);
    } catch {
      return value;
    }
  };

  const motorField = (suffix) => {
    const flat = caseDetailsLists?.[`motor_od_${suffix}`];
    if (flat != null && flat !== "") return flat;
    return caseDetailsLists?.motorCaseDetails?.[suffix] ?? "";
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("Case Summary"), path: "/Case-Summary", active: true },
        ]}
        title={t("Case Summary")}
        backButton={true}
      />

      <Row>
        <Col>
          <Card className="mb-2">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-end align-items-center mb-2 gap-1 flex-wrap">
                <Button variant="outline-info" size="sm" onClick={() => setShowCaseActivityModel(true)}>Case Activity</Button>
                <Button variant="outline-warning" size="sm" onClick={() => handleOpenCaseStatusModal("Withdrawn")}>Case Withdraw</Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleOpenCaseStatusModal("Cancel")}>Cancel Case</Button>
                <Button variant="outline-success" size="sm" onClick={() => setShowUpdateCaseDetailsModel(true)}>Edit Case</Button>

                <Button variant="outline-danger" size="sm" onClick={() => setShowAddCaseDocumentModel(true)}>Upload Document</Button>
                {caseDetailsLists?.qcStatus === false && caseDetailsLists?.caseManagerId === auth?.data?.id && (
                  <>
                    {/* <Button variant="outline-warning" size="sm" onClick={() => setShowViewCaseChecklistModel(true)}>Checklist</Button>
                    <Button variant="outline-dark" size="sm">Add New Checklist</Button> */}
                  </>
                )}

                {caseDetailsLists?.assignedStatus === true
                  && ["Waiting for Doctor Allocation", "Doctor Report Pending", "Manager QC Pending"].includes(caseDetailsLists?.case_status)
                  && (
                  <>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowAddCaseGenerateInitialReportModel(true)}>Generate Initial Report</Button>
                  </>
                )}

                {/* {caseDetailsLists?.qcStatus === true && (
                  <>
                    <Button variant="outline-warning" size="sm">View Report</Button>
                    <Button variant="outline-success" size="sm">View Final Report</Button>
                  </>
                )} */}

                <Button variant="outline-info" size="sm" onClick={() => setShowInternalTriggerModal(true)}>Internal Trigger</Button>

              </div>

              <div className="table-responsive">
                <Table bordered striped hover className="shadow-sm border-2 mb-0" style={{ fontSize: '0.875rem' }}>
                  <thead>
                    <tr className="border-1">
                      <th className="text-center py-2 px-2" style={{ width: '20%' }}>TPA Name</th>
                      <td colSpan="2" className="text-center py-2 px-2" style={{ width: '30%' }}>{caseDetailsLists?.tpaIdCaseDetails?.name}</td>
                      <th className="text-center py-2 px-2" style={{ width: '20%' }}>Company Name</th>
                      <td colSpan="2" className="text-center py-2 px-2" style={{ width: '30%' }}>{caseDetailsLists?.insuranceIdCaseDetails?.name}</td>
                    </tr>

                    <tr className="border-1">
                      <th className="text-center py-2 px-2" style={{ width: '20%' }}>Company Manager</th>
                      <td colSpan="2" className="text-center py-2 px-2" style={{ width: '20%' }}>{caseDetailsLists?.ManagerIdCaseDetails?.name}</td>
                      <th className="text-center py-2 px-2">Claim Number</th>
                      <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.claim_number}</td>
                      <th className="text-center py-2 px-2">Policy Number</th>
                      <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.policy_number}</td>
                    </tr>

                    <tr className="border-1">
                      <th className="text-center py-2 px-2">Case Type</th>
                      <td colSpan="5" className="bg-light py-2 px-2">{caseDetailsLists?.claimTypeCaseDetails?.name}</td>
                    </tr>

                    {isMotor ? (
                      <>
                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Claim Intimation Date</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("claim_intimation_date"))}</td>
                          <th className="text-center py-2 px-2">Date of Loss</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("date_of_loss"))}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Time of Accident</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("time_of_accident")}</td>
                          <th className="text-center py-2 px-2">Location</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("location")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Vehicle</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("vehicle")}</td>
                          <th className="text-center py-2 px-2">IDV</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("idv")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Date of Allocation</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("date_of_allocation"))}</td>
                          <th className="text-center py-2 px-2">Trigger for Investigation</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("trigger_for_investigation")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Insured Name</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("insured_name")}</td>
                          <th className="text-center py-2 px-2">Policy Type</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("policy_type")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Policy Period From</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("policy_period_from"))}</td>
                          <th className="text-center py-2 px-2">Policy Period To</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("policy_period_to"))}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Previous Insurer</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("previous_insurer")}</td>
                          <th className="text-center py-2 px-2">Previous Policy No.</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("previous_policy_no")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Previous Policy Expiry Date</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("previous_policy_expiry_date"))}</td>
                          <th className="text-center py-2 px-2">Registration No.</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("registration_no")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Owner Name</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("owner_name")}</td>
                          <th className="text-center py-2 px-2">RC Address</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("rc_address")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Chassis No.</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("chassis_no")}</td>
                          <th className="text-center py-2 px-2">Engine No.</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("engine_no")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">RC Valid Till</th>
                          <td colSpan="2" className="text-center py-2 px-2">{formatMotorDate(motorField("valid_till"))}</td>
                          <th className="text-center py-2 px-2">Insured Contact</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("insured_contact")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Insured Detail Name</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("insured_detail_name")}</td>
                          <th className="text-center py-2 px-2">Insured Occupation</th>
                          <td colSpan="2" className="text-center py-2 px-2">{motorField("insured_occupation")}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">TAT</th>
                          <td colSpan="2" className="text-center py-2 px-2"></td>
                          <th className="text-center py-2 px-2">Closure TAT</th>
                          <td colSpan="2" className="text-center py-2 px-2"></td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Closure Remarks</th>
                          <td colSpan="5" className="py-2 px-2"></td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Allocation Delay</th>
                          <td colSpan="5" className="py-2 px-2"></td>
                        </tr>
                      </>
                    ) : (
                      <>
                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Hospital</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.hospitaldCaseDetails?.hospital_name}</td>
                          <th className="text-center py-2 px-2">Hospital Location</th>
                          <td colSpan="2" className="text-center py-2 px-2">
                            {caseDetailsLists?.hospitaldCaseDetails?.hospital_cities?.city_name},
                            {caseDetailsLists?.hospitaldCaseDetails?.hospital_cities?.cities_state?.state_name}
                          </td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Triggers</th>
                          <td colSpan="5" className="py-2 px-2">{caseDetailsLists?.first_trigger}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Diagnosis</th>
                          <td colSpan="5" className="bg-light py-2 px-2">{caseDetailsLists?.diagnosis}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Insured Name</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.primary_name}</td>
                          <th className="text-center py-2 px-2">Insured Phone Number</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.primary_phone}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Insured Address</th>
                          <td colSpan="5" className="py-2 px-2">
                            {[
                              caseDetailsLists?.primary_address,
                              caseDetailsLists?.primaryCityCaseDetails?.city_name,
                              caseDetailsLists?.primaryCityCaseDetails?.cities_state?.state_name,
                              caseDetailsLists?.primary_pincode,
                            ].filter(Boolean).join(", ")}
                          </td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Locality</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.localityCaseDetails?.name}</td>
                          <th className="text-center py-2 px-2">Education</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.educationCaseDetails?.name}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Job</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.jobCaseDetails?.name}</td>
                          <th className="text-center py-2 px-2">Rooms</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.roomTypeCaseDetails?.name}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Income</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.incomeCaseDetails?.name}</td>
                          <th className="text-center py-2 px-2">Injury Type</th>
                          <td colSpan="2" className="text-center py-2 px-2">{caseDetailsLists?.InjuryTypeCaseDetails?.name}</td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">TAT</th>
                          <td colSpan="2" className="text-center py-2 px-2"></td>
                          <th className="text-center py-2 px-2">Closure TAT</th>
                          <td colSpan="2" className="text-center py-2 px-2"></td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Closure Remarks</th>
                          <td colSpan="5" className="py-2 px-2"></td>
                        </tr>

                        <tr className="border-1">
                          <th className="text-center py-2 px-2">Allocation Delay</th>
                          <td colSpan="5" className="py-2 px-2"></td>
                        </tr>
                      </>
                    )}
                  </thead>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* {caseDetailsLists?.generatedBy === null && (
            <Row className="mt-2">
              <Col>
                <Button variant="outline-danger" size="sm" onClick={() => setShowCaseGeneratedModel(true)}>Generate Case</Button>
              </Col>
            </Row>
          )} */}

          { caseDetailsLists?.assignBackendOfficer === null && (
            <Row className="mt-2 mb-2">
              <Col>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  disabled={allChecklistAssigned}
                  onClick={() => {
                    // Check if documents exist
                    const hasDocuments = caseDetailsLists?.case_idCaseDocumentsList && 
                                       caseDetailsLists?.case_idCaseDocumentsList?.length > 0;
                    
                    if (!hasDocuments) {
                      swal.fire({
                        title: "Warning!",
                        text: "Please upload document first",
                        icon: "warning",
                        confirmButtonText: "OK"
                      });
                    } else {
                      setShowCaseAllocationModel(true);
                    }
                  }}
                >
                  Allocate Investigator
                </Button>
              </Col>
            </Row>
          )}

          {caseDetailsLists?.assignedStatus === true && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <div className="table-responsive">
                  <Table bordered className="mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead className="thead-light">
                      <tr>
                        <th className="text-center py-2 px-2" style={{ width: '10%' }}>S.R. No.</th>
                        <th className="py-2 px-2" style={{ width: '25%' }}>Investigator Name</th>
                        <th className="py-2 px-2" style={{ width: '15%' }}>Case Status</th>
                        <th className="py-2 px-2" style={{ width: '50%' }}>Document</th>
                        <th className="py-2 px-2" style={{ width: '10%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseDetailsLists?.caseIdCaseFieldOfficerAllocation?.map((value, index) => {
                        const hasPendingChecklist = value?.fieldOfficerCaseCheckListDocument?.some(
                          (doc) => doc?.status === "Pending"
                        );

                        return (
                          <tr key={index}>
                            <td className="text-center py-2 px-2">
                              {index + 1}
                            </td>
                            <td className="py-2 px-2">
                              {value?.foIdCaseFieldOfficerAllocation?.name} ({value?.foIdCaseFieldOfficerAllocation?.adminUserType?.name})
                            </td>
                            <td className="py-2 px-2">
                              {value?.status}
                            </td>
                            <td className="py-2 px-2" style={{ fontWeight: 600, backgroundColor: "#f6f9fc", whiteSpace: "normal", wordBreak: "break-all" }}>
                              <Table size="sm" className="mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th className="py-1 px-2" style={{ fontSize: '0.8rem' }}>Document Name</th>
                                    <th className="py-1 px-2" style={{ fontSize: '0.8rem' }}>Document Status</th>
                                    <th className="py-1 px-2" style={{ fontSize: '0.8rem' }}>Document</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {value?.fieldOfficerCaseCheckListDocument?.map((valueDoc, index) => (
                                    <tr key={index}>
                                      <td className="py-1 px-2" style={{ fontSize: '0.8rem' }}>
                                        {valueDoc?.subCheckListCaseCheckListDocument?.name}
                                      </td>
                                      <td className="py-1 px-2" style={{ fontSize: '0.8rem' }}>
                                        {valueDoc?.status}
                                      </td>
                                      <td className="py-1 px-2" style={{ fontWeight: 600, backgroundColor: "#f6f9fc", whiteSpace: "normal", wordBreak: "break-all", fontSize: '0.8rem' }}>
                                        {(() => {
                                          try {
                                            const parsedDocs = typeof valueDoc?.upload_document === 'string'
                                              ? JSON.parse(valueDoc.upload_document)
                                              : valueDoc?.upload_document;

                                            return parsedDocs?.length > 0 &&
                                              parsedDocs.map((doc, docIndex) => {
                                                const documentUrl = `${docUrl}/uploads/document/${caseDetailsLists.id}/checklist/${doc.customFilename || doc.file_name}`;
                                                const fileName = doc.customFilename || doc.originalname || doc.file_name;
                                                return (
                                                  <Badge
                                                    key={docIndex}
                                                    bg="success"
                                                    className="me-1"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleDocumentClick(documentUrl, fileName)}
                                                  >
                                                    {fileName}
                                                  </Badge>
                                                );
                                              });
                                          } catch (error) {
                                            console.error('Error parsing upload_document:', error);
                                            return valueDoc?.upload_document?.length > 0 &&
                                              valueDoc?.upload_document.map((doc, docIndex) => {
                                                const documentUrl = `${docUrl}/uploads/document/${caseDetailsLists.id}/checklist/${doc.customFilename || doc.file_name}`;
                                                const fileName = doc.file_name;
                                                return (
                                                  <Badge
                                                    key={docIndex}
                                                    bg="success"
                                                    className="me-1"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleDocumentClick(documentUrl, fileName)}
                                                  >
                                                    {fileName}
                                                  </Badge>
                                                );
                                              });
                                          }
                                        })()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </td>
                            <td className="py-2 px-2 ">
                              <div className="d-flex gap-1">
                              <Button variant="outline-warning"
                                size="sm"
                                onClick={() => handleEditAllocation(value?.id)}
                                disabled={!hasPendingChecklist}> 
                                <i className="mdi mdi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleOpenRevokeModal(value)}
                                disabled={!hasPendingChecklist}
                              >
                                <i className="mdi mdi-delete"></i>
                              </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* {caseDetailsLists?.fieldQcStatus === false ?
            <Card className="mt-2">
              <Card.Body className="p-3">
                <Row>
                  <Col lg={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("Field QC Status")}</Form.Label>
                      <Select
                        options={statusList}
                        values={statusList?.filter(statusItem => statusItem?.value === formData?.qc_case_status)}
                        name="field_qc_case_status"
                        labelField="label"
                        valueField="value"
                        placeholder={t("Select Status")}
                        onChange={(selectedData) => {
                          selectHandler2(selectedData[0]?.value, "field_qc_case_status");
                        }}
                        styles={customSelectStyles}
                      />

                      {errorsCaseDetails?.data?.field_qc_case_status && (
                        <div className="invalid-feedback d-block">
                          <p className="text-danger">{errorsCaseDetails?.data?.field_qc_case_status}</p>
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={4}>
                    <FormInput
                      label={t("Field Q.C. Case Remark")}
                      type="text"
                      errors={errorsCaseDetails?.data}
                      value={formData.field_qc_case_remark || ""}
                      name="field_qc_case_remark"
                      onChange={inputHandler}
                      containerClass={"mb-2"}
                      placeholder={"Enter Case Remark"}
                    />
                  </Col>

                  <Col lg={4} className="d-flex align-items-center mt-2">
                    <Button variant="outline-success" size="sm" onClick={handleSubmitFieldQC}>Submit</Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            :
            <>
              <Card className="mt-2">
                <Card.Body className="p-3">
                  <div className="table-responsive">
                    <Table bordered className="mb-0" style={{ fontSize: '0.875rem' }}>
                      <thead className="thead-light">
                        <tr>
                          <th className="text-center py-2 px-2" style={{ width: '10%' }}>S.R. No.</th>
                          <th className="py-2 px-2" style={{ width: '30%' }}>Manager Name</th>
                          <th className="py-2 px-2" style={{ width: '20%' }}>Field QC Status</th>
                          <th className="py-2 px-2" style={{ width: '40%' }}>Field QC Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-center py-2 px-2">
                            1
                          </td>
                          <td className="py-2 px-2">
                            Admin
                          </td>
                          <td className="py-2 px-2">
                            Approved
                          </td>
                          <td className="py-2 px-2">
                            Good Case Summary
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>} */}


          {caseDetailsLists?.assignBackendOfficer !== null && caseDetailsLists?.backendCaseUploadStatus === false && currentInitialReport?.bofficer_id === auth?.data?.id && (
            <Card className="mt-2">
              <Card.Header className="d-flex justify-content-between align-items-center" style={{ padding: '0.75rem 1rem' }}>
                <div></div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setReportGenerationRequested(true);
                    dispatch(actions.getDynamicReportById(auth.token, id));
                  }}
                >
                  <i className="mdi mdi-file-pdf"></i> Generate Report
                </Button>
              </Card.Header>
              <Card.Body className="p-3">
                {currentInitialReport?.qc_case_status === "Rejected" && caseDetailsLists?.backendCaseUploadStatus === false && (
                  <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.9rem' }}>
                    <div>
                      <strong>QC Remark:</strong> {currentInitialReport?.qc_case_remark}
                    </div>
                    {currentInitialReport?.qc_documents?.length > 0 && (
                      <div className="mt-2">
                        <strong>QC Documents:</strong>
                        <div className="mt-1">
                          {renderQcDocuments(currentInitialReport.qc_documents)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <Form>
                  <Row>
                    <Col lg={4}></Col>
                    <Col lg={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("Status")}</Form.Label>
                        <Select
                          options={statusList}
                          values={statusList?.filter(statusItem => statusItem?.value === formData?.status)}
                          name="status"
                          labelField="label"
                          valueField="value"
                          placeholder={t("Select Status")}
                          onChange={(selectedData) => {
                            selectHandler2(selectedData[0]?.value, "status");
                          }}
                          styles={customSelectStyles}
                        />

                        {errorsCaseDetails?.data?.status && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsCaseDetails?.data?.status}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col lg={6}>
                      <FormInput
                        label={t("Case Remark")}
                        type="text"
                        errors={errorsCaseDetails?.data}
                        value={formData.case_remark || ""}
                        name="case_remark"
                        onChange={inputHandler}
                        containerClass={"mb-2"}
                        placeholder={"Enter Case Remark"}
                      />
                    </Col>

                    <Col lg={6}>
                      <FormInput
                        label={t("Approved / Rejected Remark")}
                        type="text"
                        errors={errorsCaseDetails?.data}
                        value={formData.case_status_remark || ""}
                        name="case_status_remark"
                        onChange={inputHandler}
                        containerClass={"mb-2"}
                        placeholder={"Enter Approved / Rejected Remark"}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                      {!reportFormatUrl && (
                        <>  
                    <Col lg={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("Case Report File")}</Form.Label>
                          <Form.Control
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={inputHandler}
                            ref={fileInputRef}
                            name="case_file"
                            style={{ maxWidth: 520 }}
                          />
                          {errorsCaseDetails?.data?.case_file && (
                            <div className="invalid-feedback d-block">
                              <p className="text-danger">{errorsCaseDetails?.data?.case_file}</p>
                            </div>
                          )}
                        </Form.Group>
                        </Col>
                    </>
                      )}

                    <Col lg={6}>
                      <FormInput
                        label={t("Approved / Rejected File")}
                        type="file"
                        errors={errorsCaseDetails?.data}
                        name="case_status_file"
                        onChange={inputHandler}
                        containerClass={"mb-2"}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col lg={12} className="text-center">
                      <Button variant="outline-success" size="sm" onClick={handleSubmitBackend}>Submit</Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}


          {caseDetailsLists?.backendCaseUploadStatus === true && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <div className="table-responsive">
                  <Table bordered className="mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead className="thead-light">
                      <tr>
                        <th className="text-center py-2 px-2" style={{ width: '8%' }}>S.R. No.</th>
                        <th className="py-2 px-2" style={{ width: '20%' }}>Backend Officer</th>
                        <th className="py-2 px-2" style={{ width: '18%' }}>Case Remark</th>
                        <th className="py-2 px-2" style={{ width: '18%' }}>Approved / Rejected Remark</th>
                        <th className="py-2 px-2" style={{ width: '18%' }}>Case Documents</th>
                        <th className="py-2 px-2" style={{ width: '18%' }}>Approved / Rejected Documents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseDetailsLists?.case_idCaseInitialReportList?.map((value, index) => (
                        <tr key={index}>
                          <td className="text-center py-2 px-2">
                            {index + 1}
                          </td>
                          <td className="py-2 px-2">
                            {value?.bofficer_idCaseInitialReport?.name} ({value?.bofficer_idCaseInitialReport?.adminUserType?.name})
                          </td>
                          <td className="py-2 px-2">
                            {value?.case_remark}
                          </td>
                          <td className="py-2 px-2">
                            {value?.case_status_remark}
                          </td>
                          <td className="py-2 px-2">
                            {reportFormatUrl ? (
                              <Badge
                                bg="info"
                                className="me-1"
                                style={{ cursor: "pointer", fontSize: "0.75rem", wordBreak: "break-all" }}
                                onClick={() => window.open(reportFormatUrl, "_blank", "noopener,noreferrer")}
                              >
                                {reportFormatUrl}
                              </Badge>
                            ) : (
                              value.case_file && (
                                <Badge
                                  bg="success"
                                  className="me-1"
                                  style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                  onClick={() => {
                                    const file = String(value.case_file ?? "");
                                    const url = /^https?:\/\//i.test(file)
                                      ? file
                                      : `${docUrl}/uploads/document/${id}/initialReport/${file}`;
                                    handleDocumentClick(url, file);
                                  }}
                                >
                                  {value.case_file}
                                </Badge>
                              )
                            )}
                          </td>
                          <td className="py-2 px-2">
                            {value.case_status_file && (
                              <Badge
                                bg="success"
                                className="me-1"
                                style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                onClick={() => {
                                  const file = String(value.case_status_file ?? "");
                                  const url = /^https?:\/\//i.test(file)
                                    ? file
                                    : `${docUrl}/uploads/document/${id}/initialReport/${file}`;
                                  handleDocumentClick(url, file);
                                }}
                              >
                                {value.case_status_file}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}


          {qcEnable === true &&
            caseDetailsLists?.backendCaseUploadStatus === true &&
            caseDetailsLists?.qcStatus === false &&
            caseDetailsLists?.caseManagerId === auth?.data?.id && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <Row>
                  <Col lg={showQcDocumentsUpload ? 3 : 4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("QC Status")}</Form.Label>
                      <Select
                        options={qcStatusList}
                        values={qcStatusList?.filter((statusItem) =>
                          formData.qc_case_status_label
                            ? statusItem.label === formData.qc_case_status_label
                            : statusItem.value === formData.qc_case_status
                        )}
                        name="qc_case_status"
                        labelField="label"
                        valueField="value"
                        placeholder={t("Select Status")}
                        onChange={(selectedData) => {
                          qcCaseStatusHandler(selectedData[0]);
                        }}
                        styles={customSelectStyles}
                      />

                      {errorsCaseDetails?.data?.qc_case_status && (
                        <div className="invalid-feedback d-block">
                          <p className="text-danger">{errorsCaseDetails?.data?.qc_case_status}</p>
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  {showQcDocumentsUpload && (
                    <Col lg={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("QC Documents")}</Form.Label>
                        <Form.Control
                          type="file"
                          multiple
                          ref={qcFileInputRef}
                          name="qc_documents"
                          onChange={qcDocumentsHandler}
                        />
                        {formData.qc_documents?.length > 0 && (
                          <div className="mt-1">
                            {formData.qc_documents.map((file, index) => (
                              <Badge key={`${file.name}-${index}`} bg="secondary" className="me-1 mb-1">
                                {file.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {errorsCaseDetails?.data?.qc_documents && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsCaseDetails?.data?.qc_documents}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  )}

                  <Col lg={showQcDocumentsUpload ? 3 : 4}>
                    <FormInput
                      label={t("QC Case Remark")}
                      type="text"
                      errors={errorsCaseDetails?.data}
                      value={formData.qc_case_remark || ""}
                      name="qc_case_remark"
                      onChange={inputHandler}
                      containerClass={"mb-2"}
                      placeholder={"Enter Case Remark"}
                    />
                  </Col>

                </Row>
                <div className="d-flex justify-content-center">
                  <Button variant="outline-success" size="sm" onClick={handleSubmitQC}>Submit</Button>
                </div>
              </Card.Body>
            </Card>
          )}


          {qcEnable === true && caseDetailsLists?.qcStatus === true && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <div className="table-responsive">
                  <Table bordered className="mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead className="thead-light">
                      <tr>
                        <th className="text-center py-2 px-2" style={{ width: '10%' }}>S.R. No.</th>
                        <th className="py-2 px-2" style={{ width: '30%' }}>Manager Name</th>
                        <th className="py-2 px-2" style={{ width: '20%' }}>QC Status</th>
                        <th className="py-2 px-2" style={{ width: '40%' }}>QC Remark</th>
                        <th className="py-2 px-2" style={{ width: '18%' }}>QC Documents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caseDetailsLists?.case_idCaseInitialReportList?.map((value, index) => (
                        <tr key={index}>
                          <td className="text-center py-2 px-2">
                            {index + 1}
                          </td>
                          <td className="py-2 px-2">
                            {value?.createdByCaseInitialReport?.name} ({value?.createdByCaseInitialReport?.adminUserType?.name})
                          </td>
                          <td className="py-2 px-2">
                            {value?.qc_case_status}
                          </td>
                          <td className="py-2 px-2">
                            {value?.qc_case_remark}
                          </td>
                          <td className="py-2 px-2">
                            {value?.qc_case_status === "Rejected"
                              ? renderQcDocuments(value?.qc_documents)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}


          {/* {caseDetailsLists?.qcStatus === true && caseDetailsLists?.gradeStatus === false && caseDetailsLists?.caseManagerId === auth?.data?.id && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <div className="text-center">
                  <Row className="mb-2">
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Rate Field Officer:</h6>
                      {renderStars("rating_fo")}
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Rate Backend Officer:</h6>
                      {renderStars("rating_bo")}
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Rate Case Document:</h6>
                      {renderStars("rating_cd")}
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Rate Case Report:</h6>
                      {renderStars("rating_cr")}
                    </Col>
                  </Row>
                  <Button variant="outline-success" size="sm" onClick={handleSubmitCaseRating}>
                    Submit
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}


          {caseDetailsLists?.gradeStatus === true && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <div className="text-center">
                  <h5 className="mb-2" style={{ fontSize: '1.1rem' }}>Submitted Ratings</h5>
                  <Row className="mb-2">
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Field Officer:</h6>
                      <div>{[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className="mx-1"
                          color={i < caseDetailsLists?.rating_fo ? "#ffc107" : "#e4e5e9"}
                          style={{ fontSize: "1.2rem" }}
                        />
                      ))}</div>
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Backend Officer:</h6>
                      <div>{[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className="mx-1"
                          color={i < caseDetailsLists?.rating_bo ? "#ffc107" : "#e4e5e9"}
                          style={{ fontSize: "1.2rem" }}
                        />
                      ))}</div>
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Case Document:</h6>
                      <div>{[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className="mx-1"
                          color={i < caseDetailsLists?.rating_cd ? "#ffc107" : "#e4e5e9"}
                          style={{ fontSize: "1.2rem" }}
                        />
                      ))}</div>
                    </Col>
                    <Col lg={3}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Case Report:</h6>
                      <div>{[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className="mx-1"
                          color={i < caseDetailsLists?.rating_cr ? "#ffc107" : "#e4e5e9"}
                          style={{ fontSize: "1.2rem" }}
                        />
                      ))}</div>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          )} */}


          {caseDetailsLists?.backendCaseUploadStatus === true &&
            caseDetailsLists?.isClose === false &&
            caseDetailsLists?.caseManagerId === auth?.data?.id &&
            (qcEnable === false || caseDetailsLists?.qcStatus === true) && (
            <Card className="mt-2">
              <Card.Body className="p-3">
                <Row>
                  <Col lg={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1" style={{ fontSize: '0.9rem' }}>{t("Is Closed")}</Form.Label>
                      <Select
                        options={isClosedStatusList}
                        values={isClosedStatusList?.filter(statusItem => statusItem?.value === formData?.case_status)}
                        name="case_status"
                        labelField="label"
                        valueField="value"
                        placeholder={t("Select Is Closed Status")}
                        onChange={(selectedData) => {
                          selectHandler2(selectedData[0]?.value, "case_status");
                        }}
                        styles={customSelectStyles}
                      />

                      {errorsCaseDetails?.data?.case_status && (
                        <div className="invalid-feedback d-block">
                          <p className="text-danger">{errorsCaseDetails?.data?.case_status}</p>
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={4} className="d-flex align-items-center mt-2">
                    <Button variant="outline-success" size="sm" onClick={handleSubmitIsClosed}>Submit</Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
      <AddCaseGenerated show={showCaseGeneratedModel} onHide={() => hideCaseGeneratedModel()} />
      <AddCaseAllocation show={showCaseAllocationModel} onHide={() => hideCaseAllocationModel()} editId={editAllocationId} />
      <UpdateCaseDetails show={showUpdateCaseDetailsModel} onHide={() => hideUpdateCaseDetailsModel()} />
      <AddCaseDocument show={showAddCaseDocumentModel} onHide={() => hideAddCaseDocumentModel()} />
      <AddCaseActivity show={showCaseActivityModel} onHide={() => hideCaseActivityModel()} />
      <AddCaseGenerateInitialReport show={showAddCaseGenerateInitialReportModel} onHide={() => hideAddCaseGenerateInitialReportModel()} />
      <ViewCaseChecklist show={showViewCaseChecklistModel} onHide={() => hideViewCaseChecklistModel()} />

      <Modal show={showRevokeModal} onHide={handleCloseRevokeModal} centered >
        <Modal.Header closeButton>
          <Modal.Title>Revoke Field Officer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="mb-1">Field Officer</Form.Label>
            <div className="font-weight-bold bg-light p-2 rounded">
              {selectedRevokeAllocation?.foIdCaseFieldOfficerAllocation?.name || "-"}
            </div>
          </Form.Group>
          <Form.Group controlId="revokeRemark">
            <Form.Label className="mb-1">Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={revokeRemark}
              onChange={(e) => setRevokeRemark(e.target.value)}
              placeholder="Enter remark"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button variant="danger" size="sm" onClick={handleRevokeAssignFieldOfficer}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCaseStatusModal} onHide={handleCloseCaseStatusModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{caseStatusModalType === "Withdrawn" ? "Case Withdraw" : "Cancel Case"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="caseStatusRemark">
            <Form.Label className="mb-1">Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={caseStatusRemark}
              onChange={(e) => setCaseStatusRemark(e.target.value)}
              placeholder="Enter remark"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button variant="primary" size="sm" onClick={handleSubmitCaseStatusModal}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>

      <FilterModal
        show={modal.show}
        onHide={() => setModal({ show: false, key: "", type: "", value: "" })}
        modalData={modal}
        onApplyFilter={handleApplyFilter}
        onRemoveFilter={handleRemoveFilter}
        onChange={handleModalChange}
      />

      <DocumentViewerModal
        show={showDocumentModal}
        onHide={hideDocumentModal}
        documentUrl={selectedDocument.url}
        fileName={selectedDocument.fileName}
      />

      {/* Internal Trigger Modal */}
      <Modal show={showInternalTriggerModal} onHide={() => setShowInternalTriggerModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Internal Trigger</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Internal Trigger</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={internalTriggerText}
              onChange={(e) => {
                setInternalTriggerText(e.target.value);
                if (internalTriggerError) {
                  setInternalTriggerError("");
                }
              }}
              placeholder="Enter Internal Trigger"
            />
            {internalTriggerError && (
              <small className="text-danger" style={{ fontSize: '0.875rem' }}>
                {internalTriggerError}
              </small>
            )}
          </Form.Group>

          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (internalTriggerText.trim()) {
                dispatch(actions.saveInternalTriggerData(auth.token, { case_id: Number(id), internal_trigger: internalTriggerText.trim() }));
                setInternalTriggerError("");
              } else {
                setInternalTriggerError("Please add internal trigger");
              }
            }}
          >
            Add Trigger
          </Button>

          {triggersList.length > 0 && (
            <div className="mt-4">
              <Table bordered striped hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>S.R. No.</th>
                    <th style={{ width: '40%' }}>Internal Trigger</th>
                    <th style={{ width: '26%' }}>Created By</th>
                    <th style={{ width: '26%' }}>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {triggersList.map((triggerItem, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>{typeof triggerItem === 'string' ? triggerItem : triggerItem.trigger}</td>
                      <td>{triggerItem.createdBy || triggerItem.createdByInternalTrigger?.name}</td>
                      <td>{typeof triggerItem === 'string' ? new Date().toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      }) : triggerItem.createdAt}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => {
            setShowInternalTriggerModal(false);
            setInternalTriggerText("");
            setInternalTriggerError("");
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {loadingCaseDetails && (<Preloader />)}
    </>
  );
};

export default CaseDetails;
